import React from 'react';
import { useApp } from '../context/AppContext';
import { formatEuro } from '../utils/format';
import {
  TrendingUp,
  ShoppingBag,
  Users,
  UtensilsCrossed,
  DollarSign,
  Clock,
  QrCode,
  Award,
  Layers,
  ArrowUpRight,
  Receipt
} from 'lucide-react';

export const AnalyticsDashboard: React.FC = () => {
  const { allOrders, diningTables, restaurantProfile } = useApp();

  // Metrics calculation
  const validOrders = allOrders.filter(o => o.status !== 'cancelled');
  const totalRevenue = validOrders.reduce((sum, o) => sum + o.total_amount, 0);
  const totalOrdersCount = validOrders.length;
  const avgOrderValue = totalOrdersCount > 0 ? totalRevenue / totalOrdersCount : 0;

  // Total VAT collected
  const totalFoodVat = validOrders.reduce((sum, o) => sum + (o.vat_food_amount || 0), 0);
  const totalBeverageVat = validOrders.reduce((sum, o) => sum + (o.vat_beverage_amount || 0), 0);

  // Table performance
  const tableCounts: Record<number, { count: number; revenue: number }> = {};
  validOrders.forEach(o => {
    if (!tableCounts[o.table_number]) {
      tableCounts[o.table_number] = { count: 0, revenue: 0 };
    }
    tableCounts[o.table_number].count += 1;
    tableCounts[o.table_number].revenue += o.total_amount;
  });

  // Top ordered items
  const itemCounts: Record<string, { count: number; revenue: number }> = {};
  validOrders.forEach(o => {
    o.items.forEach(it => {
      if (!itemCounts[it.name]) {
        itemCounts[it.name] = { count: 0, revenue: 0 };
      }
      itemCounts[it.name].count += it.quantity;
      itemCounts[it.name].revenue += it.price * it.quantity;
    });
  });

  const topItemsList = Object.entries(itemCounts)
    .map(([name, data]) => ({ name, ...data }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 5);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      {/* Header */}
      <div
        style={{
          background: 'var(--bg-secondary)',
          padding: '18px 24px',
          borderRadius: 'var(--radius-lg)',
          border: '1px solid var(--border-subtle)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '12px'
        }}
      >
        <div>
          <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: '1.35rem', fontWeight: 800 }}>
            Sales & Operational Analytics
          </h2>
          <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>
            {restaurantProfile.name} • {restaurantProfile.domain} • Business ID: {restaurantProfile.business_id}
          </div>
        </div>

        <div style={{ display: 'flex', gap: '8px' }}>
          <span
            style={{
              background: 'var(--accent-copper-light)',
              color: 'var(--accent-copper)',
              padding: '6px 14px',
              borderRadius: 'var(--radius-full)',
              fontSize: '0.85rem',
              fontWeight: 700
            }}
          >
            Live Performance Overview
          </span>
        </div>
      </div>

      {/* KPI Cards Grid */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: '16px'
        }}
      >
        {/* Total Revenue */}
        <div
          style={{
            background: 'var(--bg-card)',
            border: '1px solid var(--border-subtle)',
            borderRadius: 'var(--radius-lg)',
            padding: '20px',
            boxShadow: 'var(--shadow-sm)'
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: '0.82rem', color: 'var(--text-muted)', fontWeight: 700 }}>
              TOTAL REVENUE (INC. VAT)
            </span>
            <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: 'rgba(16,185,129,0.1)', color: 'var(--accent-emerald)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <TrendingUp size={18} />
            </div>
          </div>
          <div style={{ fontFamily: 'var(--font-heading)', fontSize: '1.8rem', fontWeight: 800, color: 'var(--text-primary)', marginTop: '8px' }}>
            {formatEuro(totalRevenue)}
          </div>
          <div style={{ fontSize: '0.78rem', color: 'var(--accent-emerald)', marginTop: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
            <ArrowUpRight size={14} />
            <span>Real-time POS synced</span>
          </div>
        </div>

        {/* Total Orders */}
        <div
          style={{
            background: 'var(--bg-card)',
            border: '1px solid var(--border-subtle)',
            borderRadius: 'var(--radius-lg)',
            padding: '20px',
            boxShadow: 'var(--shadow-sm)'
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: '0.82rem', color: 'var(--text-muted)', fontWeight: 700 }}>
              TOTAL ORDERS
            </span>
            <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: 'rgba(224,90,54,0.1)', color: 'var(--accent-copper)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <ShoppingBag size={18} />
            </div>
          </div>
          <div style={{ fontFamily: 'var(--font-heading)', fontSize: '1.8rem', fontWeight: 800, color: 'var(--text-primary)', marginTop: '8px' }}>
            {totalOrdersCount}
          </div>
          <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: '4px' }}>
            Table QR dine-in transactions
          </div>
        </div>

        {/* Average Order Value */}
        <div
          style={{
            background: 'var(--bg-card)',
            border: '1px solid var(--border-subtle)',
            borderRadius: 'var(--radius-lg)',
            padding: '20px',
            boxShadow: 'var(--shadow-sm)'
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: '0.82rem', color: 'var(--text-muted)', fontWeight: 700 }}>
              AVERAGE ORDER VALUE
            </span>
            <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: 'rgba(56,189,248,0.1)', color: '#38bdf8', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Receipt size={18} />
            </div>
          </div>
          <div style={{ fontFamily: 'var(--font-heading)', fontSize: '1.8rem', fontWeight: 800, color: 'var(--text-primary)', marginTop: '8px' }}>
            {formatEuro(avgOrderValue)}
          </div>
          <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: '4px' }}>
            Per dining table session
          </div>
        </div>

        {/* Total Finnish VAT */}
        <div
          style={{
            background: 'var(--bg-card)',
            border: '1px solid var(--border-subtle)',
            borderRadius: 'var(--radius-lg)',
            padding: '20px',
            boxShadow: 'var(--shadow-sm)'
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: '0.82rem', color: 'var(--text-muted)', fontWeight: 700 }}>
              TOTAL VAT TAX BREAKDOWN
            </span>
            <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: 'rgba(245,158,11,0.1)', color: 'var(--accent-amber)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Layers size={18} />
            </div>
          </div>
          <div style={{ fontFamily: 'var(--font-heading)', fontSize: '1.8rem', fontWeight: 800, color: 'var(--accent-copper)', marginTop: '8px' }}>
            {formatEuro(totalFoodVat + totalBeverageVat)}
          </div>
          <div style={{ fontSize: '0.74rem', color: 'var(--text-muted)', marginTop: '4px' }}>
            Food 14%: {formatEuro(totalFoodVat)} | Alcohol/Bev 25.5%: {formatEuro(totalBeverageVat)}
          </div>
        </div>
      </div>

      {/* Two Column Section: Top Dishes & Table Performance */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
          gap: '20px'
        }}
      >
        {/* Top Selling Dishes */}
        <div
          style={{
            background: 'var(--bg-card)',
            border: '1px solid var(--border-subtle)',
            borderRadius: 'var(--radius-lg)',
            padding: '20px',
            boxShadow: 'var(--shadow-sm)'
          }}
        >
          <h3 style={{ fontSize: '1.1rem', fontWeight: 800, marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Award size={18} color="var(--accent-amber)" />
            <span>Top 5 Best-Selling Dishes</span>
          </h3>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {topItemsList.map((item, idx) => (
              <div
                key={item.name}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '10px 14px',
                  background: 'var(--bg-secondary)',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--border-subtle)'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ fontWeight: 800, color: 'var(--accent-copper)', fontSize: '0.9rem', width: '20px' }}>
                    #{idx + 1}
                  </span>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: '0.88rem' }}>{item.name}</div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                      {item.count} portions ordered
                    </div>
                  </div>
                </div>
                <div style={{ fontWeight: 800, fontSize: '0.92rem', color: 'var(--text-primary)' }}>
                  {formatEuro(item.revenue)}
                </div>
              </div>
            ))}
            {topItemsList.length === 0 && (
              <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem', textAlign: 'center', padding: '20px' }}>
                No completed dish sales yet
              </div>
            )}
          </div>
        </div>

        {/* Table Performance */}
        <div
          style={{
            background: 'var(--bg-card)',
            border: '1px solid var(--border-subtle)',
            borderRadius: 'var(--radius-lg)',
            padding: '20px',
            boxShadow: 'var(--shadow-sm)'
          }}
        >
          <h3 style={{ fontSize: '1.1rem', fontWeight: 800, marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <QrCode size={18} color="var(--accent-copper)" />
            <span>Table Performance Breakdown</span>
          </h3>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {diningTables.map(tbl => {
              const data = tableCounts[tbl.table_number] || { count: 0, revenue: 0 };
              return (
                <div
                  key={tbl.id}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '10px 14px',
                    background: 'var(--bg-secondary)',
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid var(--border-subtle)'
                  }}
                >
                  <div>
                    <div style={{ fontWeight: 700, fontSize: '0.88rem' }}>
                      Table #{tbl.table_number} ({tbl.capacity} Seats)
                    </div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                      {data.count} order(s) submitted
                    </div>
                  </div>
                  <div style={{ fontWeight: 800, fontSize: '0.92rem', color: 'var(--accent-copper)' }}>
                    {formatEuro(data.revenue)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};
